<template>
  <div class="view force-password-change">
    <div class="container">
      <div class="top">
        <div class="title">{{ $t('forcePasswordChangeTitle') }}</div>
        <div class="subtitle">{{ $t('forcePasswordChangeSubtitle') }}</div>
      </div>

      <div class="form-container">
        <input
          id="newPassword"
          v-model="newPassword"
          type="password"
          :class="{ incorrect: isValidated && newPassword !== newPasswordConfirmation }"
          class="input"
          name="newPassword"
          :placeholder="$t('newPassword')"
          @click="reset"
        />

        <input
          id="newPasswordConfirmation"
          v-model="newPasswordConfirmation"
          type="password"
          :class="{ incorrect: isValidated && newPassword !== newPasswordConfirmation }"
          class="input"
          name="newPasswordConfirmation"
          :placeholder="$t('newPasswordConfirmation')"
          @click="reset"
          @keydown.enter="validate"
        />
      </div>

      <div v-if="errorMessage" class="error-message" :class="{ active: errorMessage }">
        {{ errorMessage }}
      </div>

      <div class="button main-button save-button" @click="validate">
        {{ $t('save') }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import roles from '@/utils/roles';

export default {
  data() {
    return {
      newPassword: '',
      newPasswordConfirmation: '',
      isValidated: false,
      errorMessage: '',
    };
  },

  computed: {
    ...mapGetters(['user', 'newPasswordVerificationToken']),
  },

  methods: {
    reset() {
      this.isValidated = false;
      this.errorMessage = '';
    },

    async validate() {
      this.errorMessage = '';
      this.isValidated = true;

      await this.checkValidation(this.changePassword);
    },

    async checkValidation(callback) {
      this.errorMessage = '';

      if (this.newPassword.length !== 0 && this.newPassword === this.newPasswordConfirmation) {
        await callback();
      } else if (this.newPassword.length === 0 || this.newPasswordConfirmation.length === 0) {
        this.errorMessage = this.$t('pleaseFillInAllFields');
      } else {
        this.errorMessage = this.$t('passDontMatch');
      }
    },

    async changePassword() {
      try {
        await this.$store.dispatch('resetPassword', {
          password: this.newPassword,
          token: this.newPasswordVerificationToken,
        });
        await this.$store.dispatch('clearExpiredPassword');
        this.$router.push({ name: 'home' });
      } catch (e) {
        this.errorMessage = this.$t('anErrorOccured');

        if (e.response.status === 400 && e.response.data.code === 400900) {
          this.errorMessage = this.$t('passwordNotCompliant', {
            numberOfCharacters: this.user.role === roles.Admin ? 14 : 12,
          });
        }

        if (e.response.status === 409 && e.response.data.code === 409002) {
          this.errorMessage = this.$t('passwordShouldBeDifferentFromOldOne');
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';

.view.force-password-change {
  background: colors.$main-color;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 20px;

  .container {
    max-width: calc(100% - 32px);
    width: 590px;
    height: auto;
    padding: 32px 96px;
    box-sizing: border-box;
    background: colors.$white-color;
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(colors.$darkblue-color, 0.2);

    @media screen and (max-width: 700px) {
      padding: 48px 32px;
    }

    .top {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      margin: 0 0 32px;

      .title {
        font-family: fonts.$title-font;
        color: colors.$darkblue-color;
        font-weight: 500;
        font-size: 24px;
        line-height: 29px;
        margin-bottom: 8px;
      }

      .subtitle {
        font-family: fonts.$default-font;
        font-size: 14px;
        color: colors.$grey-color;
      }
    }

    .form-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      margin: 0 0 16px;

      .input {
        width: 100%;
        height: 48px;
        margin: 0 0 16px;
        padding: 12px 16px;
        box-sizing: border-box;
        color: colors.$text-color;
        border: solid 1px colors.$lightgrey-color;
        border-radius: 4px;
        background: colors.$white-color;

        &:last-child {
          margin: 0;
        }

        &::placeholder {
          color: colors.$lightgrey-color;
        }

        &.incorrect {
          border-color: colors.$error-color;
        }
      }
    }

    .error-message {
      visibility: hidden;
      height: 32px;
      margin: -8px 0 8px;
      font-size: 12px;
      color: colors.$error-color;

      &.active {
        visibility: visible;
      }
    }

    .save-button {
      width: 0;
      margin: 32px auto 0;
    }
  }
}
</style>
